import { useEffect, useMemo, useState } from 'react';
import Modal from './ui/Modal.jsx';
import { pb } from '../lib/pocketbase.js';
import { useAuth } from '../auth/AuthContext.jsx';
import {
  PRAXIS_ART_OPTIONS,
  PRAXIS_STATUS_OPTIONS,
  PRAXIS_TYP_OPTIONS,
  VERSORGUNGSFORM_OPTIONS,
  VERBUND_OPTIONS,
} from '../lib/praxis.js';
import { FACHRICHTUNG_OPTIONS } from '../lib/fachrichtung.js';

const EMPTY_REGEL = {
  status: 'alle',
  praxis_art: 'alle',
  versorgungsform: 'alle',
  verbund: 'alle',
  typen: [],
  fachgebiete: [],
  plz_von: '',
  plz_bis: '',
  nur_hauptpraxen: false,
};

const EMPTY_FORM = {
  name: '',
  beschreibung: '',
  filter_regel: EMPTY_REGEL,
};

function normalisiereRegel(regel) {
  // Falls aus älteren Datensätzen Felder fehlen, mit Defaults auffüllen,
  // damit das Formular keine controlled→uncontrolled-Warnings wirft.
  // Bestehende Selektionen ohne nur_hauptpraxen-Property bekommen false
  // (= alle Praxen drin, Filialen inklusive) — abwärtskompatibel.
  return {
    ...EMPTY_REGEL,
    ...(regel ?? {}),
    typen: Array.isArray(regel?.typen) ? regel.typen : [],
    fachgebiete: Array.isArray(regel?.fachgebiete) ? regel.fachgebiete : [],
    nur_hauptpraxen: regel?.nur_hauptpraxen === true,
  };
}

function istPlzValide(value) {
  if (!value) return true;
  return /^\d{1,5}$/.test(value);
}

export default function SelektionFormModal({ open, onClose, onSaved, selektion }) {
  const { user } = useAuth();
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const isEdit = Boolean(selektion?.id);

  useEffect(() => {
    if (!open) return;
    if (selektion) {
      setForm({
        name: selektion.name ?? '',
        beschreibung: selektion.beschreibung ?? '',
        filter_regel: normalisiereRegel(selektion.filter_regel),
      });
    } else {
      setForm(EMPTY_FORM);
    }
    setError(null);
  }, [open, selektion]);

  function updateField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function updateRegel(key, value) {
    setForm((prev) => ({
      ...prev,
      filter_regel: { ...prev.filter_regel, [key]: value },
    }));
  }

  function toggleFachgebiet(slug) {
    setForm((prev) => {
      const liste = prev.filter_regel.fachgebiete ?? [];
      const next = liste.includes(slug)
        ? liste.filter((s) => s !== slug)
        : [...liste, slug];
      return {
        ...prev,
        filter_regel: { ...prev.filter_regel, fachgebiete: next },
      };
    });
  }

  function toggleTyp(value) {
    setForm((prev) => {
      const liste = prev.filter_regel.typen ?? [];
      const next = liste.includes(value)
        ? liste.filter((v) => v !== value)
        : [...liste, value];
      return {
        ...prev,
        filter_regel: { ...prev.filter_regel, typen: next },
      };
    });
  }

  const regel = form.filter_regel;

  const plzVonValid = istPlzValide(regel.plz_von);
  const plzBisValid = istPlzValide(regel.plz_bis);

  const plzBereichValide = useMemo(() => {
    if (!regel.plz_von || !regel.plz_bis) return true;
    return regel.plz_von <= regel.plz_bis;
  }, [regel.plz_von, regel.plz_bis]);

  async function handleSubmit(event) {
    event.preventDefault();
    const name = form.name.trim();
    if (!name) {
      setError('Bitte einen Namen für die Selektion vergeben.');
      return;
    }
    if (!plzVonValid || !plzBisValid) {
      setError('PLZ darf nur Ziffern enthalten (max. 5 Stellen).');
      return;
    }
    if (!plzBereichValide) {
      setError('PLZ "von" muss kleiner oder gleich "bis" sein.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        name,
        beschreibung: form.beschreibung,
        filter_regel: {
          status: regel.status,
          praxis_art: regel.praxis_art,
          versorgungsform: regel.versorgungsform,
          verbund: regel.verbund,
          typen: regel.typen,
          fachgebiete: regel.fachgebiete,
          plz_von: regel.plz_von.trim(),
          plz_bis: regel.plz_bis.trim(),
          nur_hauptpraxen: regel.nur_hauptpraxen === true,
        },
      };
      let saved;
      if (isEdit) {
        saved = await pb.collection('selektionen').update(selektion.id, payload);
      } else {
        payload.erstellt_von = user?.id;
        payload.aktiv = true;
        saved = await pb.collection('selektionen').create(payload);
      }
      onSaved?.(saved);
      onClose();
    } catch (err) {
      setError(err?.message || 'Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? 'Selektion bearbeiten' : 'Neue Selektion'}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label className="label" htmlFor="selektion-name">
            Name *
          </label>
          <input
            id="selektion-name"
            type="text"
            required
            maxLength={100}
            className="input mt-1"
            value={form.name}
            onChange={(e) => updateField('name', e.target.value)}
            placeholder="z. B. Hamburger Hausärzte ohne Erstkontakt"
          />
        </div>

        <div>
          <label className="label" htmlFor="selektion-beschreibung">
            Beschreibung
          </label>
          <textarea
            id="selektion-beschreibung"
            rows={3}
            className="input mt-1"
            value={form.beschreibung}
            onChange={(e) => updateField('beschreibung', e.target.value)}
            placeholder="Worum geht es bei dieser Liste? Was ist das Ziel?"
          />
        </div>

        <div className="rounded-md border border-slate-200 bg-slate-50 p-4">
          <h3 className="text-sm font-semibold text-slate-700">Filter</h3>
          <p className="mt-1 text-xs text-slate-500">
            Die Selektion aktualisiert sich automatisch — neue Praxen, die zum
            Filter passen, erscheinen ohne Zutun in der Liste.
          </p>

          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="label" htmlFor="filter-status">
                Status
              </label>
              <select
                id="filter-status"
                className="input mt-1"
                value={regel.status}
                onChange={(e) => updateRegel('status', e.target.value)}
              >
                <option value="alle">Alle</option>
                {PRAXIS_STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="label" htmlFor="filter-praxis-art">
                Praxis-Art
              </label>
              <select
                id="filter-praxis-art"
                className="input mt-1"
                value={regel.praxis_art}
                onChange={(e) => updateRegel('praxis_art', e.target.value)}
              >
                <option value="alle">Alle</option>
                {PRAXIS_ART_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="label" htmlFor="filter-versorgungsform">
                Versorgungsform
              </label>
              <select
                id="filter-versorgungsform"
                className="input mt-1"
                value={regel.versorgungsform}
                onChange={(e) => updateRegel('versorgungsform', e.target.value)}
              >
                <option value="alle">Alle</option>
                {VERSORGUNGSFORM_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="label" htmlFor="filter-verbund">
                Verbund
              </label>
              <select
                id="filter-verbund"
                className="input mt-1"
                value={regel.verbund}
                onChange={(e) => updateRegel('verbund', e.target.value)}
              >
                <option value="alle">Alle</option>
                <option value="keiner">Kein Verbund</option>
                {VERBUND_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="label" htmlFor="filter-plz-von">
                PLZ von
              </label>
              <input
                id="filter-plz-von"
                type="text"
                inputMode="numeric"
                pattern="\d{0,5}"
                maxLength={5}
                className={`input mt-1 ${!plzVonValid ? 'border-red-400' : ''}`}
                value={regel.plz_von}
                onChange={(e) => updateRegel('plz_von', e.target.value)}
                placeholder="z. B. 20000"
              />
            </div>

            <div>
              <label className="label" htmlFor="filter-plz-bis">
                PLZ bis
              </label>
              <input
                id="filter-plz-bis"
                type="text"
                inputMode="numeric"
                pattern="\d{0,5}"
                maxLength={5}
                className={`input mt-1 ${!plzBisValid ? 'border-red-400' : ''}`}
                value={regel.plz_bis}
                onChange={(e) => updateRegel('plz_bis', e.target.value)}
                placeholder="z. B. 24999"
              />
            </div>
          </div>

          <div className="mt-4">
            <span className="label">Typ</span>
            <p className="mt-1 text-xs text-slate-500">
              Keine Auswahl = Arztpraxen und Kliniken.{' '}
              {regel.typen.length > 0 && (
                <button
                  type="button"
                  onClick={() => updateRegel('typen', [])}
                  className="font-medium text-primary-600 hover:text-primary-800"
                >
                  Auswahl zurücksetzen
                </button>
              )}
            </p>
            <div className="mt-2 flex flex-wrap gap-3">
              {PRAXIS_TYP_OPTIONS.map((opt) => {
                const checked = regel.typen.includes(opt.value);
                return (
                  <label
                    key={opt.value}
                    className="flex cursor-pointer items-center gap-2 rounded border border-slate-200 bg-white px-3 py-1.5 text-sm hover:bg-slate-50"
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                      checked={checked}
                      onChange={() => toggleTyp(opt.value)}
                    />
                    <span className="text-slate-700">{opt.label}</span>
                  </label>
                );
              })}
            </div>
          </div>

          <div className="mt-4">
            <label className="flex cursor-pointer items-start gap-2 rounded border border-slate-200 bg-white p-3 hover:bg-slate-50">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                checked={regel.nur_hauptpraxen === true}
                onChange={(e) => updateRegel('nur_hauptpraxen', e.target.checked)}
              />
              <span className="text-sm">
                <span className="font-medium text-slate-800">Nur Hauptpraxen anzeigen</span>
                <span className="mt-0.5 block text-xs text-slate-500">
                  Filialen werden ausgeschlossen — sinnvoll, wenn die
                  Entscheidung typischerweise an der Hauptpraxis fällt.
                  Einzelpraxen bleiben in der Selektion.
                </span>
              </span>
            </label>
          </div>

          <div className="mt-4">
            <span className="label">Fachgebiete</span>
            <p className="mt-1 text-xs text-slate-500">
              Keine Auswahl = alle Fachgebiete.{' '}
              {regel.fachgebiete.length > 0 && (
                <button
                  type="button"
                  onClick={() => updateRegel('fachgebiete', [])}
                  className="font-medium text-primary-600 hover:text-primary-800"
                >
                  Auswahl zurücksetzen
                </button>
              )}
            </p>
            <div className="mt-2 max-h-56 overflow-y-auto rounded-md border border-slate-200 bg-white p-3">
              <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                {FACHRICHTUNG_OPTIONS.map((opt) => {
                  const checked = regel.fachgebiete.includes(opt.value);
                  return (
                    <label
                      key={opt.value}
                      className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm hover:bg-slate-50"
                    >
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                        checked={checked}
                        onChange={() => toggleFachgebiet(opt.value)}
                      />
                      <span className="text-slate-700">{opt.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="rounded-md bg-red-50 p-3 text-sm text-red-700 ring-1 ring-red-200">
            {error}
          </div>
        )}

        <div className="sticky bottom-0 -mx-6 -mb-5 mt-6 flex justify-end gap-2 border-t border-slate-200 bg-white px-6 py-4">
          <button
            type="button"
            className="btn-secondary"
            onClick={onClose}
            disabled={saving}
          >
            Abbrechen
          </button>
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? 'Speichern …' : isEdit ? 'Speichern' : 'Selektion anlegen'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
